import {
  VIDEO_TABLE_INFO,
  VIDEO_TABLE_INFO_PARAMS,
} from '@/utils/const/products/vod-assessment';
import { Select, Table, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import RadiusTrendImg from './AssessmentTemplates/RadiusTrendImg';
import Styles from './index.module.scss';
import InfoTooltip from './InfoTooltip';

interface sourceVideoConfig {
  type: string;
  configs: {
    resolution: string;
    bitRate: number;
    KVQ: number;
    VMAF: number;
  }[];
}

interface IProps {
  [index: string]: any;
  sourceVideoConfigs: sourceVideoConfig[];
  defaultIndex?: number;
  onChange?: Function;
}

const VideoInfoTable = ({
  sourceVideoConfigs,
  defaultIndex = 0,
  onChange,
  ...props
}: IProps) => {
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [currentCommonIndex, setCurrentCommonIndex] = useState(defaultIndex);
  const [currentZZQLIndex, setCurrentZZQLIndex] = useState(defaultIndex);
  const [currentZZYYIndex, setCurrentZZYYIndex] = useState(defaultIndex);
  let configOptions = {};
  const columnConfig = [
    {
      type: 'common',
      column: {
        title: () => {
          return (
            <div style={{ display: 'flex', alignItems: 'middle' }}>
              <span>{VIDEO_TABLE_INFO.common.name}</span>
              <InfoTooltip
                title={VIDEO_TABLE_INFO.common.tooltip}
                style={{ position: 'relative', top: 1 }}
              />
            </div>
          );
        },
        dataIndex: 'common',
        render: (val, record, index) =>
          selectCellRender('common', { val, record, index }),
        width: 130,
      },
    },
    {
      type: 'zzql',
      column: {
        title: () => {
          return (
            <>
              <span>{VIDEO_TABLE_INFO.zzql.name}</span>
              <InfoTooltip
                style={{ position: 'relative', top: 1 }}
                title={VIDEO_TABLE_INFO.zzql.tooltip}
              />
            </>
          );
        },
        dataIndex: 'zzql',
        render: (val, record, index) =>
          selectCellRender('zzql', { val, record, index }),
        width: 130,
      },
    },
    {
      type: 'zzyy',
      column: {
        title: () => {
          return (
            <>
              <span>{VIDEO_TABLE_INFO.zzyy.name}</span>
              <InfoTooltip title={VIDEO_TABLE_INFO.zzyy.tooltip} />
            </>
          );
        },
        dataIndex: 'zzyy',
        render: (val, record, index) =>
          selectCellRender('zzyy', { val, record, index }),
        width: 130,
      },
    },
  ];
  // 指标名称提示
  const indicatorInfos = {
    KVQ: VIDEO_TABLE_INFO_PARAMS.KVQ.tooltip,
    VMAF: VIDEO_TABLE_INFO_PARAMS.VMAF.tooltip,
  };

  // table cell render
  const selectCellRender = (type: string, { val, record, index }) => {
    // 第一行渲染 select
    if (index === 0) {
      const options = configOptions[type]?.map((item) => item.resolution);
      return (
        <Select
          value={val}
          bordered={false}
          onChange={(val) => {
            const index = options.findIndex((item) => item === val);
            videoConfigChange(type, index);
          }}
          className={Styles.tableInfoSelect}
        >
          {options?.map((item) => (
            <Select.Option value={item} key={item}>
              {item}
            </Select.Option>
          ))}
        </Select>
      );
    } else if (type === 'zzql') {
      // zzql 渲染带 img
      return (
        <>
          <RadiusTrendImg
            type={
              val !== record.common
                ? parseFloat(val) > parseFloat(record.common)
                  ? 'up'
                  : 'down'
                : ''
            }
          />
          <span>{val}</span>
        </>
      );
    }

    return val;
  };

  function videoConfigChange(type, index) {
    if (type === 'common') {
      setCurrentCommonIndex(index);
    } else if (type === 'zzql') {
      setCurrentZZQLIndex(index);
    } else if (type === 'zzyy') {
      setCurrentZZYYIndex(index);
    }
  }

  function videoChange() {
    // 格式化 selectOptions
    const configOptionsTemp = {};
    sourceVideoConfigs.forEach((item) => {
      configOptionsTemp[item.type] = item.configs;
    });
    configOptions = configOptionsTemp;
    // columns
    const videoTypes = sourceVideoConfigs.map((item) => item.type);
    const tempColumns: any = [
      {
        title: '',
        dataIndex: 'fieldName',
        render: (val) => (
          <>
            <span className={Styles.tableInfoLabel}>{val}</span>
            {indicatorInfos[val] && <InfoTooltip title={indicatorInfos[val]} />}
          </>
        ),
        width: 100,
      },
      { title: VIDEO_TABLE_INFO.origin.name, dataIndex: 'origin', width: 100 },
    ];
    columnConfig.forEach((item) => {
      if (videoTypes.includes(item.type)) {
        tempColumns.push(item.column);
      }
    });
    // 数据内容
    const originVideoConfig = sourceVideoConfigs.find(
      (item) => item.type === 'origin'
    )?.configs?.[0];
    const commonVideoConfig = sourceVideoConfigs.find(
      (item) => item.type === 'common'
    )?.configs?.[currentCommonIndex];
    const zzqlVideoConfig = sourceVideoConfigs.find(
      (item) => item.type === 'zzql'
    )?.configs?.[currentZZQLIndex];
    const zzyyVideoConfig = sourceVideoConfigs.find(
      (item) => item.type === 'zzyy'
    )?.configs?.[currentZZYYIndex];

    const data = [
      {
        fieldName: VIDEO_TABLE_INFO_PARAMS.resolution.name,
        origin: originVideoConfig.resolution,
        common: commonVideoConfig?.resolution,
        zzql: zzqlVideoConfig?.resolution,
        zzyy: zzyyVideoConfig?.resolution,
      },
      {
        fieldName: VIDEO_TABLE_INFO_PARAMS.bitRate.name,
        origin: `${originVideoConfig.bitRate}kbps`,
        common: `${commonVideoConfig?.bitRate}kbps`,
        zzql: `${zzqlVideoConfig?.bitRate}kbps`,
        zzyy: `${zzyyVideoConfig?.bitRate}kbps`,
      },
      {
        fieldName: VIDEO_TABLE_INFO_PARAMS.KVQ.name,
        origin: originVideoConfig.KVQ,
        common: commonVideoConfig?.KVQ,
        zzql: zzqlVideoConfig?.KVQ,
        zzyy: zzyyVideoConfig?.KVQ,
      },
      {
        fieldName: VIDEO_TABLE_INFO_PARAMS.VMAF.name,
        origin: originVideoConfig.VMAF,
        common: commonVideoConfig?.VMAF,
        zzql: zzqlVideoConfig?.VMAF,
        zzyy: zzyyVideoConfig?.VMAF,
      },
    ];

    setColumns(tempColumns);
    if (onChange) {
      onChange(data);
    }
    setTableData(data);
  }
  useEffect(() => {
    videoChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentZZQLIndex,
    currentZZYYIndex,
    currentCommonIndex,
    sourceVideoConfigs,
  ]);

  return (
    <div className={Styles.tableInfo}>
      <Table
        {...props}
        dataSource={tableData}
        columns={columns}
        pagination={false}
        rowKey="fieldName"
      />
    </div>
  );
};

export default VideoInfoTable;
